/**
 * KSK2022_R01 라벨 데이터 빌더
 * Store 데이터에서 라벨 출력용 데이터를 추출
 */

import { LabelPrintData } from "src/apis/labelApi";
import { formatDate } from "src/utils/string/stringUtil";
import { KSK2022_R01DetailDto, KSK2022_R01HeaderDto, KSK2022_R01LotDto } from "../Dto_KSK2022_R01";

export interface KSK2022_R01StoreData {
	KSK2022_R01LotList: KSK2022_R01LotDto[];
	KSK2022_R01DetailList: KSK2022_R01DetailDto[];
	editKSK2022_R01: KSK2022_R01HeaderDto | null;
}

const ITEM_NM_MAX_LEN = 14;

const formatQty = (qty: number | undefined, unit: string): string => {
	if (!qty && qty !== 0) return '';
	return `${qty.toLocaleString()}${unit || 'EA'}`;
};

/**
 * LOT 정보와 Store 데이터를 기반으로 라벨 출력 데이터 생성 (MES 라벨 규격과 동일)
 */
export const KSK2022_R01_buildLabelData = (
	lotNo: string,
	itemCd: string,
	store: KSK2022_R01StoreData
): LabelPrintData => {
	const {
		KSK2022_R01LotList,
		KSK2022_R01DetailList,
		editKSK2022_R01
	} = store;

	const lot = KSK2022_R01LotList.find((l: any) => l.lotNo === lotNo);
	const header = editKSK2022_R01;

	if (!lot) throw new Error(`LOT를 찾을 수 없습니다: ${lotNo}`);
	if (!header) throw new Error('입고 정보를 찾을 수 없습니다');

	const transportDate = header.productDt
		? formatDate(new Date(header.productDt))
		: formatDate(new Date());
	const storageLocation = (header as any).locationCd?.trim() || (lot as any).locationCd?.trim() || '-';
	let itemNm = (header as any).itemNm ?? '';
	if (itemNm.length > ITEM_NM_MAX_LEN) itemNm = itemNm.substring(0, ITEM_NM_MAX_LEN);

	return {
		lotNo: lot.lotNo || '',
		itemCd: header.itemCd || '',
		itemNm,
		itemSpec: header.itemSpec || '',
		transportDate,
		storageLocation,
		customer: (header as any).customerNm ?? (header as any).customNm ?? '',
		customerItemCd: (header as any).customerItemCd ?? '',
		qty: formatQty(lot.lotQty, header.itemUnit || lot.itemUnit || 'EA'),
		itemAccountNm: header.itemAccountNm ?? '',
		labelType: '생산입고',
	};
};

/**
 * valueIndex로 LOT를 찾아서 라벨 데이터 생성
 */
export const KSK2022_R01_buildLabelDataByValueIndex = (
	valueIndex: number,
	itemCd: string,
	store: KSK2022_R01StoreData
): LabelPrintData | null => {
	const { KSK2022_R01LotList } = store;
	const lot = KSK2022_R01LotList.find((l: KSK2022_R01LotDto) => l.valueIndex === valueIndex);

	if (!lot) {
		console.warn(`LOT를 찾을 수 없습니다: valueIndex=${valueIndex}, itemCd=${itemCd}`);
		return null;
	}

	try {
		return KSK2022_R01_buildLabelData(lot.lotNo, itemCd, store);
	} catch (error) {
		console.error('라벨 데이터 생성 실패:', error);
		return null;
	}
};

/**
 * 전체 LotMap에서 미출력 LOT 라벨 데이터 추출 (저장 후 자동 출력용)
 */
export const KSK2022_R01_extractUnprintedLabelData = (
	store: KSK2022_R01StoreData,
	deletedLotList: string[] = []
): LabelPrintData[] => {
	const { KSK2022_R01LotList, editKSK2022_R01 } = store;
	const header = editKSK2022_R01;
	const unprintedLabelData: LabelPrintData[] = [];
	const deletedSet = new Set(deletedLotList);

	KSK2022_R01LotList.forEach((lot: KSK2022_R01LotDto) => {
		if (
			!deletedSet.has(lot.valueIndex?.toString() || '') &&
			lot.lotQty > 0 &&
			lot.inserted === true
		) {
			unprintedLabelData.push(KSK2022_R01_buildLabelData(lot.lotNo, header!.itemCd, store));
		}
	});

	return unprintedLabelData;
};
